// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Variable expansion — `${OTHER_VAR}` inside a `.env` value.
 *
 * THE VALUE IN THE BUNDLE IS NOT THE VALUE IN THE FILE. Vite runs dotenv-expand, so
 * `VITE_EXPANDED=prefix-${VITE_STRIPE_PK}-suffix` ships as `prefix-pk_live_51Qw…-suffix`. Searching
 * for the literal text of the file finds nothing and reports a clean bill of health.
 *
 * Worse, the engines disagree with each other and Vite has shipped three of them:
 *   - dotenv-expand v11  (Vite 5)
 *   - dotenv-expand v12/13 (Vite 6/7/8)
 *   - @next/env's vendored v5-era engine (Next.js)
 * Measured divergences include `tok_dollarX${_NOPE}Y_end`, which one engine leaves alone and
 * another mutates to `tok_dollarXY_end`, and `pass$1word`, which two engines truncate to `pass`.
 *
 * So all three run and every DISTINCT output is enrolled as a needle, labelled with the engine that
 * produced it. Only values containing `$` can diverge, so the cost is near zero.
 *
 * Command substitution `$(…)` is never executed by any of them — only the squatted
 * `dotenv-expand@1000.0.0` runs `execSync`, and no Vite version depends on it. We do not execute
 * anything either, obviously; we treat `$(` as literal.
 */

export type ExpandEngine = 'raw' | 'dotenv-expand-11' | 'dotenv-expand-13' | 'next-env-5';

const MAX_DEPTH = 8;

/** dotenv-expand v11, as shipped with Vite 5. Supports `${VAR}`, `$VAR`, and `${VAR:-default}`. */
const RE_11 = /(\\)?(\$)(?!\()(\{?)([\w.]+)(?::?-((?:\$\{[^}]*\}|[^}])+))?(\}?)/gi;

/** dotenv-expand v12/13, as shipped with Vite 6/7/8. Unbraced names must start with a letter or _. */
const RE_13 = /(?<!\\)\$\{([^{}]+)\}|(?<!\\)\$([A-Za-z_][A-Za-z0-9_]*)/g;

function lookup(env: Map<string, string>, name: string): string | undefined {
  return env.get(name);
}

function expand11(value: string, env: Map<string, string>, depth = 0): string {
  if (depth > MAX_DEPTH || !value.includes('$')) return value;
  const out = value.replace(RE_11, (_all, escaped, _dollar, open, name, fallback, close) => {
    if (escaped) return `$${open}${name}${fallback ? `:-${fallback}` : ''}${close}`;
    // An opened brace with no closing brace is not a reference in v11.
    if (open && !close) return `$${open}${name}${fallback ? `:-${fallback}` : ''}`;
    const found = lookup(env, name);
    if (found !== undefined && found !== '') return found;
    if (fallback !== undefined) return expand11(fallback, env, depth + 1);
    return '';
  });
  return out === value ? out : expand11(out, env, depth + 1);
}

function expand13(value: string, env: Map<string, string>, depth = 0): string {
  if (depth > MAX_DEPTH || !value.includes('$')) return value;
  const out = value.replace(RE_13, (_all, braced, bare) => {
    const name: string = braced ?? bare;
    if (braced !== undefined) {
      // v13 supports `${VAR:-default}` inside the braces.
      const colon = name.indexOf(':-');
      if (colon > 0) {
        const key = name.slice(0, colon);
        const found = lookup(env, key);
        if (found !== undefined && found !== '') return found;
        return expand13(name.slice(colon + 2), env, depth + 1);
      }
    }
    const found = lookup(env, name);
    return found === undefined ? '' : found;
  });
  return out === value ? out : expand13(out, env, depth + 1);
}

/**
 * @next/env's vendored engine — a dotenv-expand v5-era `_interpolate`. Notably it has NO
 * `${VAR:-default}` support, so a value using one expands differently from the Vite engines.
 */
function expandNext5(value: string, env: Map<string, string>, depth = 0): string {
  if (depth > MAX_DEPTH || !value.includes('$')) return value;
  const out = value.replace(/(.?)\$(?:\{([\w.]+)\}|([\w.]+))/g, (all, prefix, braced, bare) => {
    if (prefix === '\\') return all.slice(1);
    const name: string = braced ?? bare;
    const found = lookup(env, name);
    return `${prefix}${found === undefined ? '' : found}`;
  });
  return out === value ? out : expandNext5(out, env, depth + 1);
}

export interface Expansion {
  readonly value: string;
  readonly engine: ExpandEngine;
}

/**
 * Every distinct expansion of `value`, including the raw string, each labelled with the engine that
 * produced it. The raw form is ALWAYS included — a bundler that does no expansion is a real
 * configuration and the literal is then what shipped.
 */
export function expansions(value: string, env: Map<string, string>): Expansion[] {
  const out: Expansion[] = [{ value, engine: 'raw' }];
  if (!value.includes('$')) return out;
  const seen = new Set([value]);
  const push = (v: string, engine: ExpandEngine) => {
    if (seen.has(v)) return;
    seen.add(v);
    out.push({ value: v, engine });
  };
  try {
    push(expand11(value, env), 'dotenv-expand-11');
  } catch {
    /* a pathological value must not stop the scan */
  }
  try {
    push(expand13(value, env), 'dotenv-expand-13');
  } catch {
    /* as above */
  }
  try {
    push(expandNext5(value, env), 'next-env-5');
  } catch {
    /* as above */
  }
  return out;
}

/**
 * True when expansion emptied a non-empty value. That is not a leak finding — it is a CONFIG BUG
 * finding: the variable referenced something undefined, so the build shipped an empty string where
 * the author expected a credential.
 */
export function isEmptiedByExpansion(raw: string, expanded: Expansion[]): boolean {
  return raw !== '' && expanded.some((e) => e.engine !== 'raw' && e.value === '');
}
